import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment/moment';
import { useAppSelector } from 'src/store';
import { setStoredActiveSettingsId } from 'src/store/slices/configurationSlice';
import { useGetAllConfigurationSettingsQuery } from 'src/services';

// Owns the list of a project's configuration settings versions: paginated
// fetching/merging, and resolving which version is "active" - the user's
// last stored choice, else the deployed version, else the most recent one -
// persisting that choice back to the store.
export const useConfigurationSettingsList = (id: string, defaultTemplateFields: any) => {
  const dispatch = useDispatch();
  const configuration = useAppSelector((s) => s.configurationSlice);

  const [settingsPage, setSettingsPage] = useState(1);
  const [settings, setSettings] = useState<any[]>([]);
  const [activeSettings, setActiveSettings] = useState<any>(null);
  const [isCreationMode, setIsCreationMode] = useState(true);

  const { data: allConfigurationSettings, isLoading: isLoadingAllConfigurationSettings } =
    useGetAllConfigurationSettingsQuery(
      { id, page: settingsPage },
      { skip: !Boolean(id && defaultTemplateFields) },
    ) as any;

  useEffect(() => {
    if (!allConfigurationSettings) {
      return;
    }

    setSettings((prevState) => {
      const newState = [...prevState];

      allConfigurationSettings.items.forEach((item) => {
        const index = prevState.findIndex((prevItem) => prevItem.id === item.id);

        if (index > -1) {
          newState[index] = item;
        } else {
          newState.push(item);
        }
      });

      newState.sort((a, b) => (moment(b.created_at) as any) - (moment(a.created_at) as any));

      return newState;
    });

    if (allConfigurationSettings.page_info.has_next_page) {
      setSettingsPage(allConfigurationSettings.page_info.page + 1);
    }
  }, [allConfigurationSettings]);

  useEffect(() => {
    if (!defaultTemplateFields) {
      return;
    }

    const deployedSettings = settings.find((item) => item.is_deployed);
    const storedSettings =
      configuration[id] &&
      settings.find((item) => item.id === configuration[id]?.storedActiveSettingsId);

    if (!activeSettings && storedSettings) {
      setActiveSettings(storedSettings);
      return;
    }

    if (!activeSettings && deployedSettings) {
      setActiveSettings(deployedSettings);
      return;
    }

    if (!activeSettings && settings.length) {
      setActiveSettings(settings[0]);
    }

    if (activeSettings) {
      setActiveSettings(settings.find((item) => item.id === activeSettings.id));
    }
  }, [settings, defaultTemplateFields]);

  useEffect(() => {
    if (!activeSettings) {
      return;
    }

    setIsCreationMode(
      typeof activeSettings.is_deployed === 'boolean' ? activeSettings.is_deployed : true,
    );
    dispatch(setStoredActiveSettingsId({ id, storedActiveSettingsId: activeSettings.id }));
  }, [activeSettings]);

  const resetToFirstPage = () => setSettingsPage(1);

  // Used when cancelling out of "new version" creation mode (activeSettings
  // was cleared): puts the active version back using the same priority order
  // as the resolution effect above.
  const restoreActiveSettingsFallback = () => {
    if (activeSettings) {
      return;
    }

    const deployedSettings = settings.find((item) => item.is_deployed);
    const storedSettings =
      configuration[id] &&
      settings.find((item) => item.id === configuration[id]?.storedActiveSettingsId);
    const fallback = storedSettings || deployedSettings || settings[0];

    if (fallback) {
      setActiveSettings(fallback);
    }
  };

  return {
    settings,
    activeSettings,
    setActiveSettings,
    isCreationMode,
    setIsCreationMode,
    isLoadingAllConfigurationSettings,
    resetToFirstPage,
    restoreActiveSettingsFallback,
  };
};
